import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  GlobeEuropeAfricaIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  ShoppingBagIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
  ResponsiveContainer,
  LineChart,
  Line,
  ComposedChart,
  Area,
  Scatter,
  ScatterChart,
  ZAxis,
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis
} from 'recharts';

const MarketComparisonAnalysis = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedTimeframe, setSelectedTimeframe] = useState('year');
  const [marketData, setMarketData] = useState({
    marketShare: [],
    competitorComparison: [],
    regionalPerformance: [],
    priceAnalysis: [],
    productCategoryComparison: [],
    trendsComparison: []
  });

  // Couleurs pour les graphiques
  const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  // Régions disponibles
  const regions = [
    { id: 'all', name: 'Toutes les régions' },
    { id: 'north', name: 'Nord' },
    { id: 'east', name: 'Est' },
    { id: 'west', name: 'Ouest' },
    { id: 'south', name: 'Sud' },
    { id: 'center', name: 'Centre' }
  ];

  // Périodes disponibles
  const timeframes = [
    { id: 'month', name: 'Ce mois' },
    { id: 'quarter', name: 'Ce trimestre' },
    { id: 'year', name: 'Cette année' },
    { id: 'all', name: 'Toutes les périodes' }
  ];

  // Générer des données fictives pour l'analyse du marché
  useEffect(() => {
    // Simuler un chargement
    setIsLoading(true);

    // Générer les données après un délai
    const timer = setTimeout(() => {
      generateMarketData();
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [selectedRegion, selectedTimeframe]);

  const generateMarketData = () => {
    // Données de part de marché
    const marketShareData = [
      { name: 'Votre entreprise', value: 28 },
      { name: 'Concurrent A', value: 22 },
      { name: 'Concurrent B', value: 17 },
      { name: 'Concurrent C', value: 15 },
      { name: 'Autres', value: 18 }
    ];

    // Données de comparaison des concurrents
    const competitorData = [
      {
        name: 'Prix',
        'Votre entreprise': 80,
        'Concurrent A': 85,
        'Concurrent B': 70,
        'Concurrent C': 90
      },
      {
        name: 'Qualité',
        'Votre entreprise': 85,
        'Concurrent A': 80,
        'Concurrent B': 75,
        'Concurrent C': 70
      },
      {
        name: 'Service',
        'Votre entreprise': 90,
        'Concurrent A': 75,
        'Concurrent B': 80,
        'Concurrent C': 85
      },
      {
        name: 'Innovation',
        'Votre entreprise': 75,
        'Concurrent A': 70,
        'Concurrent B': 90,
        'Concurrent C': 65
      },
      {
        name: 'Disponibilité',
        'Votre entreprise': 85,
        'Concurrent A': 80,
        'Concurrent B': 75,
        'Concurrent C': 90
      }
    ];

    // Données de performance régionale
    const regionalData = [
      { region: 'Nord', ventes: 1250000, croissance: 12, clients: 450, part: 32 },
      { region: 'Est', ventes: 980000, croissance: 8, clients: 320, part: 25 },
      { region: 'Ouest', ventes: 1100000, croissance: 15, clients: 380, part: 28 },
      { region: 'Sud', ventes: 750000, croissance: 5, clients: 210, part: 20 },
      { region: 'Centre', ventes: 1500000, croissance: 18, clients: 520, part: 35 }
    ];

    // Filtrer par région si nécessaire
    const filteredRegionalData = selectedRegion === 'all'
      ? regionalData
      : regionalData.filter(item => item.region.toLowerCase() === selectedRegion);

    // Données d'analyse des prix
    const priceData = [
      { category: 'Suppléments', 'Votre prix moyen': 3500, 'Prix moyen du marché': 3800 },
      { category: 'Vêtements', 'Votre prix moyen': 2800, 'Prix moyen du marché': 3200 },
      { category: 'Équipements', 'Votre prix moyen': 12000, 'Prix moyen du marché': 13500 },
      { category: 'Accessoires', 'Votre prix moyen': 1500, 'Prix moyen du marché': 1800 },
      { category: 'Chaussures', 'Votre prix moyen': 6500, 'Prix moyen du marché': 7200 }
    ];

    // Données de comparaison par catégorie de produit
    const categoryData = [
      {
        category: 'Suppléments',
        'Votre part': 35,
        'Concurrent A': 25,
        'Concurrent B': 20,
        'Concurrent C': 15,
        'Autres': 5
      },
      {
        category: 'Vêtements',
        'Votre part': 20,
        'Concurrent A': 30,
        'Concurrent B': 15,
        'Concurrent C': 25,
        'Autres': 10
      },
      {
        category: 'Équipements',
        'Votre part': 25,
        'Concurrent A': 20,
        'Concurrent B': 30,
        'Concurrent C': 15,
        'Autres': 10
      },
      {
        category: 'Accessoires',
        'Votre part': 30,
        'Concurrent A': 15,
        'Concurrent B': 25,
        'Concurrent C': 20,
        'Autres': 10
      },
      {
        category: 'Chaussures',
        'Votre part': 15,
        'Concurrent A': 35,
        'Concurrent B': 20,
        'Concurrent C': 20,
        'Autres': 10
      }
    ];

    // Données de tendances comparatives
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
    const trendsData = months.map(month => {
      // Générer des valeurs aléatoires pour simuler des tendances
      const yourGrowth = Math.floor(Math.random() * 10) + 5; // Entre 5% et 15%
      const marketGrowth = Math.floor(Math.random() * 8) + 3; // Entre 3% et 11%
      
      return {
        month,
        'Votre croissance': yourGrowth,
        'Croissance du marché': marketGrowth,
        'Différence': yourGrowth - marketGrowth
      };
    });

    // Mettre à jour l'état avec toutes les données générées
    setMarketData({
      marketShare: marketShareData,
      competitorComparison: competitorData,
      regionalPerformance: filteredRegionalData,
      priceAnalysis: priceData,
      productCategoryComparison: categoryData,
      trendsComparison: trendsData
    });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <div className="w-16 h-16 border-4 border-t-blue-500 border-white/20 rounded-full animate-spin"></div>
        <p className="mt-4 text-white/60">Analyse du marché en cours...</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* En-tête */}
      <div className="flex items-center space-x-4 mb-6">
        <button
          onClick={() => navigate('/advanced-analytics')}
          className="p-2 bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 text-white hover:bg-white/20 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white">Analyse Comparative du Marché</h1>
          <p className="text-white/60 text-sm mt-1">
            Comparez vos performances avec celles du marché et de vos concurrents
          </p>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-4 bg-white/5 p-4 rounded-xl border border-white/10">
        <div>
          <label className="text-white/60 text-sm mb-1 block">Région</label>
          <select
            value={selectedRegion}
            onChange={(e) => setSelectedRegion(e.target.value)}
            className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {regions.map((region) => (
              <option key={region.id} value={region.id}>{region.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-white/60 text-sm mb-1 block">Période</label>
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {timeframes.map((timeframe) => (
              <option key={timeframe.id} value={timeframe.id}>{timeframe.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Statistiques de marché */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white/5 backdrop-blur-lg rounded-xl p-5 border border-white/20">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <GlobeEuropeAfricaIcon className="h-5 w-5 text-blue-400" />
            </div>
            <h3 className="text-lg font-medium text-white">Part de marché</h3>
          </div>
          <p className="text-3xl font-bold text-white mt-3">28%</p>
          <div className="flex items-center mt-1 text-sm">
            <ArrowTrendingUpIcon className="h-4 w-4 text-green-400 mr-1" />
            <span className="text-green-400">+3.5%</span>
            <span className="text-white/60 ml-1">vs année précédente</span>
          </div>
        </div>

        <div className="bg-white/5 backdrop-blur-lg rounded-xl p-5 border border-white/20">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <BuildingOfficeIcon className="h-5 w-5 text-green-400" />
            </div>
            <h3 className="text-lg font-medium text-white">Position</h3>
          </div>
          <p className="text-3xl font-bold text-white mt-3">#1</p>
          <p className="text-white/60 text-sm mt-1">Leader sur 3 catégories</p>
        </div>

        <div className="bg-white/5 backdrop-blur-lg rounded-xl p-5 border border-white/20">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <ShoppingBagIcon className="h-5 w-5 text-purple-400" />
            </div>
            <h3 className="text-lg font-medium text-white">Avantage prix</h3>
          </div>
          <p className="text-3xl font-bold text-white mt-3">-8.2%</p>
          <p className="text-white/60 text-sm mt-1">vs prix moyen du marché</p>
        </div>

        <div className="bg-white/5 backdrop-blur-lg rounded-xl p-5 border border-white/20">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-amber-500/20 rounded-lg">
              <UserGroupIcon className="h-5 w-5 text-amber-400" />
            </div>
            <h3 className="text-lg font-medium text-white">Fidélité clients</h3>
          </div>
          <p className="text-3xl font-bold text-white mt-3">76%</p>
          <div className="flex items-center mt-1 text-sm">
            <ArrowTrendingUpIcon className="h-4 w-4 text-green-400 mr-1" />
            <span className="text-green-400">+5.3%</span>
            <span className="text-white/60 ml-1">vs concurrents</span>
          </div>
        </div>
      </div>

      {/* Graphiques d'analyse de marché */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Part de marché */}
        <div className="bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/20">
          <h3 className="text-lg font-medium text-white mb-4">Répartition des parts de marché</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={marketData.marketShare}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis type="number" stroke="rgba(255,255,255,0.6)" />
                <YAxis dataKey="name" type="category" stroke="rgba(255,255,255,0.6)" />
                <Tooltip
                  formatter={(value) => [`${value}%`, 'Part de marché']}
                  contentStyle={{
                    backgroundColor: 'rgba(30, 41, 59, 0.9)',
                    borderColor: 'rgba(255,255,255,0.2)',
                    color: 'white'
                  }}
                />
                <Bar dataKey="value" fill="#8884d8">
                  {marketData.marketShare.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Comparaison des concurrents */}
        <div className="bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/20">
          <h3 className="text-lg font-medium text-white mb-4">Comparaison avec les concurrents</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart outerRadius={90} data={marketData.competitorComparison}>
                <PolarGrid stroke="rgba(255,255,255,0.2)" />
                <PolarAngleAxis dataKey="name" stroke="rgba(255,255,255,0.6)" />
                <PolarRadiusAxis stroke="rgba(255,255,255,0.6)" angle={30} domain={[0, 100]} />
                <Radar name="Votre entreprise" dataKey="Votre entreprise" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                <Radar name="Concurrent A" dataKey="Concurrent A" stroke="#82ca9d" fill="#82ca9d" fillOpacity={0.6} />
                <Radar name="Concurrent B" dataKey="Concurrent B" stroke="#ffc658" fill="#ffc658" fillOpacity={0.6} />
                <Radar name="Concurrent C" dataKey="Concurrent C" stroke="#ff8042" fill="#ff8042" fillOpacity={0.6} />
                <Legend wrapperStyle={{ color: 'white' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(30, 41, 59, 0.9)',
                    borderColor: 'rgba(255,255,255,0.2)',
                    color: 'white'
                  }}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Analyse des prix */}
        <div className="bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/20">
          <h3 className="text-lg font-medium text-white mb-4">Analyse comparative des prix</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={marketData.priceAnalysis}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="category" stroke="rgba(255,255,255,0.6)" />
                <YAxis stroke="rgba(255,255,255,0.6)" />
                <Tooltip
                  formatter={(value) => `${value.toLocaleString()} DA`}
                  contentStyle={{
                    backgroundColor: 'rgba(30, 41, 59, 0.9)',
                    borderColor: 'rgba(255,255,255,0.2)',
                    color: 'white'
                  }}
                />
                <Legend wrapperStyle={{ color: 'white' }} />
                <Bar dataKey="Votre prix moyen" fill="#8884d8" />
                <Bar dataKey="Prix moyen du marché" fill="#82ca9d" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Tendances comparatives */}
        <div className="bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/20">
          <h3 className="text-lg font-medium text-white mb-4">Tendances de croissance comparatives</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={marketData.trendsComparison}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="month" stroke="rgba(255,255,255,0.6)" />
                <YAxis stroke="rgba(255,255,255,0.6)" />
                <Tooltip
                  formatter={(value) => `${value}%`}
                  contentStyle={{
                    backgroundColor: 'rgba(30, 41, 59, 0.9)',
                    borderColor: 'rgba(255,255,255,0.2)',
                    color: 'white'
                  }}
                />
                <Legend wrapperStyle={{ color: 'white' }} />
                <Area type="monotone" dataKey="Différence" fill="#8884d8" stroke="#8884d8" fillOpacity={0.3} />
                <Line type="monotone" dataKey="Votre croissance" stroke="#ff7300" strokeWidth={2} />
                <Line type="monotone" dataKey="Croissance du marché" stroke="#82ca9d" strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Performance régionale */}
      <div className="bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/20">
        <h3 className="text-xl font-semibold text-white mb-6">Performance par région</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/10">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Région</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Ventes</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Croissance</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Clients</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Part de marché</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {marketData.regionalPerformance.map((region, index) => (
                <tr key={index} className="hover:bg-white/5">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <MapPinIcon className="h-5 w-5 text-blue-400 mr-2" />
                      <span className="text-white">{region.region}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-white">
                    {region.ventes.toLocaleString()} DA
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="flex items-center justify-center">
                      {region.croissance > 10 ? (
                        <ArrowTrendingUpIcon className="h-4 w-4 text-green-400 mr-1" />
                      ) : (
                        <ArrowTrendingUpIcon className="h-4 w-4 text-yellow-400 mr-1" />
                      )}
                      <span className={region.croissance > 10 ? 'text-green-400' : 'text-yellow-400'}>
                        {region.croissance}%
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-white">
                    {region.clients}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-white">
                    {region.part}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Insights et recommandations */}
      <div className="bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/20">
        <h3 className="text-xl font-semibold text-white mb-4">Insights et Recommandations</h3>

        <div className="space-y-4">
          <div className="p-4 bg-gradient-to-r from-blue-500/20 to-blue-700/20 rounded-xl border border-blue-500/30">
            <h4 className="font-medium text-blue-300 mb-2">Positionnement sur le marché</h4>
            <p className="text-sm text-white/80">
              Votre entreprise maintient une position de leader avec 28% de part de marché, 
              3.5% au-dessus de l'année précédente. Vous surpassez votre plus proche concurrent 
              de 6 points. Continuez à capitaliser sur vos forces en matière de service et de qualité.
            </p>
          </div>

          <div className="p-4 bg-gradient-to-r from-green-500/20 to-green-700/20 rounded-xl border border-green-500/30">
            <h4 className="font-medium text-green-300 mb-2">Avantage concurrentiel</h4>
            <p className="text-sm text-white/80">
              Votre stratégie de prix vous donne un avantage compétitif avec des prix moyens 8.2% 
              inférieurs au marché. Cet avantage est particulièrement marqué dans les catégories 
              Vêtements (-12.5%) et Accessoires (-16.7%). Maintenez cette stratégie tout en veillant 
              à préserver vos marges.
            </p>
          </div>

          <div className="p-4 bg-gradient-to-r from-amber-500/20 to-amber-700/20 rounded-xl border border-amber-500/30">
            <h4 className="font-medium text-amber-300 mb-2">Opportunités régionales</h4>
            <p className="text-sm text-white/80">
              La région Centre présente la plus forte croissance (+18%) et la plus grande part de marché (35%). 
              La région Sud montre le potentiel de croissance le plus important avec seulement 20% de part de marché 
              et une croissance modérée de 5%. Envisagez d'intensifier vos efforts marketing et votre présence 
              dans cette région.
            </p>
          </div>

          <div className="p-4 bg-gradient-to-r from-purple-500/20 to-purple-700/20 rounded-xl border border-purple-500/30">
            <h4 className="font-medium text-purple-300 mb-2">Menaces concurrentielles</h4>
            <p className="text-sm text-white/80">
              Le Concurrent B surpasse tous les acteurs du marché en matière d'innovation (90/100) et 
              gagne des parts dans la catégorie Équipements (30% contre votre 25%). Surveillez de près 
              leurs lancements de produits et envisagez d'accélérer votre cycle d'innovation dans cette catégorie.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default MarketComparisonAnalysis;